import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Box } from "@mui/material";
import { useDealer, useSettings } from "@/contexts";
import { DealerAvatar } from "@/components/atoms";
import DealerSearch from "../DealerSearch";
import { ReactComponent as AvatarImage } from "../../../assets/DTV Logo.svg";
import { colors } from "@/theme";
import * as S from "./styles";
import { useDataApi } from "@/hooks";
import { isCode128, isDigit } from "@/utils";
import { ErrorMessage } from "@/components/atoms";
import CircularProgress from "@mui/material/CircularProgress";

export type ScanDealerCodePageProps = {
  // Keep compatibility with previous Dialog props
  open?: boolean;
  onClose?: () => void;
  // Add new page-specific props
  onComplete?: () => void;
};

const ScanDealerCodePage: React.FC<ScanDealerCodePageProps> = ({
  open = true, // Default to true for page usage
  onClose = undefined,
  onComplete = undefined,
}) => {
  const navigate = useNavigate();
  const { dealer, setDealer } = useDealer();
  const { settings } = useSettings();
  const { data, isLoading, error, setUrl } = useDataApi();
  const baseUrl = `${process.env.REACT_APP_REST_BASE_URL}/dealers`;
  const [inputValue, setInputValue] = useState("");
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [showError, setShowError] = useState(false);

  // Create a debounced version of handleAdminPageButtonClick
  const debouncedHandleAdminPageButtonClick = useCallback(() => {
    if (!inputValue) {
      return;
    }

    const dealerBadgeCodeLengths = Array.isArray(
      settings?.dealerBadgeCodeLength
    )
      ? settings?.dealerBadgeCodeLength
      : [settings?.dealerBadgeCodeLength];

    if (!Array.isArray(dealerBadgeCodeLengths)) {
      return;
    }

    let isMatch = false;

    for (let i = 0, l = dealerBadgeCodeLengths.length; i < l; ++i) {
      const dealerBadgeCodeLength = dealerBadgeCodeLengths[i];

      if (inputValue?.length === dealerBadgeCodeLength) {
        isMatch = true;
        break;
      }
    }

    if (!isMatch) {
      setShowError(true);
      return;
    }

    console.log('Making API call for dealer ID:', inputValue);
    setUrl(`${baseUrl}/bybadge/${inputValue}`);
  }, [inputValue, settings, baseUrl, setUrl]);

  // Set up the debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue) {
        debouncedHandleAdminPageButtonClick();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue, debouncedHandleAdminPageButtonClick]);

  useEffect(() => {
    if (dealer) {
      // Call both callbacks for compatibility
      onClose?.();
      onComplete?.();
    }
  }, [dealer, onClose, onComplete]);

  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    if (data) {
      console.log('Dealer data received:', data);
      setDealer(data);
      // Navigate directly after setting dealer
      console.log('Navigating to /login/channels');
      navigate("/login/channels");
    }
  }, [data, isLoading, error, setDealer]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("scan-dealer-page-active");
    } else {
      document.body.classList.remove("scan-dealer-page-active");
    }

    return () => {
      document.body.classList.remove("scan-dealer-page-active");
    };
  }, [open]);

  // If dealer exists or dialog is closed, don't render
  if (dealer || !open) {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsInputEmpty(!value);
    setShowError(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isInputEmpty) {
      debouncedHandleAdminPageButtonClick();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100%",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1000,
        backgroundColor: colors.neutralGrey300,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "95%",
          height: "85%",
          // maxWidth: "500px",
          padding: "40px 20px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          backgroundColor: colors.white,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "50%",
            justifyContent: "center",
            height: "100%",
            gap: "50px",
          }}
        >
          <Box sx={{ marginTop: "48px" }}>
            <AvatarImage />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box sx={{ marginTop: "24px", width: "100%" }}>
              <DealerSearch 
                autoFocus={true} 
                onChange={handleInputChange} 
                onKeyPress={handleKeyPress}
              />
            </Box>

            {showError && (
              <Box sx={{ margin: "36px 0" }}>
                <ErrorMessage errorMessage="Invalid dealer code ID" />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "32px",
              width: "100%",
            }}
          >
            <Button
              onClick={debouncedHandleAdminPageButtonClick}
              disabled={isInputEmpty || isLoading}
              sx={{
                color: isLoading
                  ? colors.white
                  : !isInputEmpty
                  ? colors.white
                  : colors.neutralGrey700,
                background: isLoading
                  ? colors.neutralGrey500
                  : !isInputEmpty
                  ? colors.ozone
                  : colors.neutralGrey400,
                borderRadius: "5px",
                border: `2px solid ${
                  isLoading
                    ? colors.neutralGrey500
                    : !isInputEmpty
                    ? colors.ozone
                    : colors.neutralGrey200
                }`,
                padding: "24px 48px 24px 48px",
                fontFamily: "PF DIN Text Pro",
                fontSize: "22px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "16px",
                textTransform: "capitalize",
                "&.Mui-disabled": {
                  color: colors.neutralGrey500,
                  background: colors.neutralGrey200,
                  border: `2px solid ${colors.neutralGrey200}`,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={28} sx={{ color: colors.primaryOzone500 }} />
              ) : (
                "Enter"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScanDealerCodePage;
